import { Select, SimpleGrid } from '@mantine/core'
import useSWR from 'swr'
import { API_URLS } from '../config/api'
import { type RegionValue, selectDistrict, selectProvince, selectRegency, toRegionOptions } from '../lib/regionCascade'

const fetcher = (url: string) => fetch(url).then((res) => res.json())

type Props = {
  value: RegionValue
  onChange: (value: RegionValue) => void
  required?: boolean
}

export type { RegionValue }

/**
 * Cascading select provinsi → kabupaten/kota → kecamatan.
 * Anak hanya di-fetch saat induknya terpilih; memilih induk mereset anak
 * (logika di lib/regionCascade agar teruji terpisah).
 */
export function RegionCascadeSelect({ value, onChange, required }: Props) {
  const { data: provinceRes, isLoading: loadingProvinces } = useSWR(API_URLS.getProvinces(), fetcher)
  const { data: regencyRes, isLoading: loadingRegencies } = useSWR(
    value.idProvince ? API_URLS.getRegencies(value.idProvince) : null,
    fetcher,
  )
  const { data: districtRes, isLoading: loadingDistricts } = useSWR(
    value.idRegency ? API_URLS.getDistricts(value.idRegency) : null,
    fetcher,
  )

  return (
    <>
      <Select
        label="Province"
        placeholder="Select province"
        data={toRegionOptions(provinceRes?.data)}
        value={value.idProvince || null}
        onChange={(id) => onChange(selectProvince(id))}
        disabled={loadingProvinces}
        searchable
        required={required}
        mt="sm"
      />
      <SimpleGrid cols={2} spacing="md" mt="sm">
        <Select
          label="Regency / City"
          placeholder={value.idProvince ? 'Select regency/city' : 'Select province first'}
          data={toRegionOptions(regencyRes?.data)}
          value={value.idRegency || null}
          onChange={(id) => onChange(selectRegency(value, id))}
          disabled={!value.idProvince || loadingRegencies}
          searchable
          required={required}
        />
        <Select
          label="District"
          placeholder={value.idRegency ? 'Select district' : 'Select regency/city first'}
          data={toRegionOptions(districtRes?.data)}
          value={value.idDistrict || null}
          onChange={(id) => onChange(selectDistrict(value, id))}
          disabled={!value.idRegency || loadingDistricts}
          searchable
          required={required}
        />
      </SimpleGrid>
    </>
  )
}
